import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import FlowWorking from '@/components/auth/flow/components/screens/Flow.Working';
import FlowStuck from '@/components/auth/flow/components/screens/Flow.Stuck';
import FlowPassword from '@/components/auth/flow/components/screens/Flow.Password';
import FlowNewPassword from '@/components/auth/flow/components/screens/Flow.New.Password';
import FlowCode from '@/components/auth/flow/components/screens/Flow.Code';
import FlowRecoveryCode from '@/components/auth/flow/components/screens/Flow.Recovery.Code';
import FlowTotpSetup from '@/components/auth/flow/components/screens/Flow.Totp.Setup';
import FlowPasskey from '@/components/auth/flow/components/screens/Flow.Passkey';
import FlowProfile from '@/components/auth/flow/components/screens/Flow.Profile';
import FlowRecoveryCodes from '@/components/auth/flow/components/screens/Flow.Recovery.Codes';
import flowMessageOf from '@/function/auth/flow.message.of';
import passkeyAvailable from '@/function/auth/passkey.available';
import type { TypeAuthFlow } from '@/types/auth/types.auth.flow';

type Props = {
    flow: TypeAuthFlow;
    start?: React.ReactNode;
    done?: React.ReactNode;
    onRestart: () => void;
    onForgot?: () => void;
    newPasswordLabel?: string;
};

const ENTER = { opacity: 0, y: 8 };
const SHOWN = { opacity: 1, y: 0, transition: { duration: 0.22, ease: 'easeOut' as const } };
const LEAVE = { opacity: 0, y: -6, transition: { duration: 0.16, ease: 'easeIn' as const } };

const screenKey = (flow: TypeAuthFlow): string => {
    const { screen } = flow;
    return 'step' in screen ? `${screen.id}:${screen.step}` : screen.id;
};

const AuthFlow: React.FC<Props> = ({ flow, start, done, onRestart, onForgot, newPasswordLabel }) => {
    const reduced = useReducedMotion();
    const passkeySupported = React.useMemo(() => passkeyAvailable(), []);
    const { screen } = flow;
    const message = React.useMemo(() => (flow.failure ? flowMessageOf(flow.failure, screen.id) : null), [flow.failure, screen.id]);
    const props = { flow, message };

    const body = (): React.ReactNode => {
        switch (screen.id) {
            case 'idle': return start ?? (flow.failure ? <FlowStuck failure={flow.failure} code={flow.failure.code} onRestart={onRestart} /> : <FlowWorking />);
            case 'working': return <FlowWorking />;
            case 'done': return done ?? <FlowWorking label="Fatto: un attimo…" />;
            case 'stuck': return <FlowStuck failure={flow.failure} code={screen.code} onRestart={onRestart} />;
            case 'recovery-codes': return <FlowRecoveryCodes flow={flow} codes={screen.codes} />;
            case 'password': return <FlowPassword {...props} step={screen.step} passkeySupported={passkeySupported} onForgot={onForgot} />;
            case 'new-password': return <FlowNewPassword {...props} step={screen.step} passkeyStep={screen.passkeyStep} passkeySupported={passkeySupported} submitLabel={newPasswordLabel} />;
            case 'email-code': return <FlowCode {...props} step={screen.step} mode="email" destination={screen.destination} passkeySupported={passkeySupported} />;
            case 'totp': return <FlowCode {...props} step={screen.step} mode="totp" destination={null} passkeySupported={passkeySupported} />;
            case 'recovery-code': return <FlowRecoveryCode {...props} step={screen.step} passkeySupported={passkeySupported} />;
            case 'totp-setup': return <FlowTotpSetup {...props} step={screen.step} uri={screen.uri} secret={screen.secret} />;
            case 'passkey': return <FlowPasskey {...props} step={screen.step} registering={screen.registering} ceremony={screen.ceremony} passkeySupported={passkeySupported} />;
            case 'profile': return <FlowProfile {...props} step={screen.step} />;
            default: return <FlowStuck failure={null} code="STEP_NOT_ALLOWED" onRestart={onRestart} />;
        }
    };

    return (
        <AnimatePresence mode="wait" initial={false}>
            <motion.div
                key={screenKey(flow)}
                initial={reduced ? false : ENTER}
                animate={reduced ? { opacity: 1 } : SHOWN}
                exit={reduced ? { opacity: 1 } : LEAVE}
            >
                {body()}
            </motion.div>
        </AnimatePresence>
    );
};

export default AuthFlow;
